"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import Image from "next/image";
import { motion } from "motion/react";
import { ArrowDown, ArrowRight, Check, Palette } from "lucide-react";
import { Link } from "@/navigation";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { type QrTemplatePreviewLocale, STORED_QR_TEMPLATE_PRESETS, getStoredQrTemplatePreviewImageUrl } from "@/lib/qr-template-registry";
interface PocketCardsShowcaseProps {
  locale: string;
  autoExpand?: boolean;
  squareCardPreviews?: boolean;
  copy?: Partial<{
    badge: string;
    title: string;
    body: string;
    subtitle: string;
    bottomNote: string;
  }>;
  expandedCardIds?: string[];
  stackCardIds?: string[];
  theme?: {
    sectionStyle?: React.CSSProperties;
    badgeStyle?: React.CSSProperties;
    titleStyle?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;
    subtitleStyle?: React.CSSProperties;
    loginButtonClassName?: string;
    loginButtonStyle?: React.CSSProperties;
    loginButtonVariant?: React.ComponentProps<typeof Button>["variant"];
    expandButtonClassName?: string;
    expandButtonStyle?: React.CSSProperties;
  };
}
const supportedLocales = new Set(["en", "da", "no", "sv", "de"]);
const DEFAULT_EXPANDED_CARD_IDS = ["balloons-qr-card", "default-qr-card", "eucalyptus-qr-card", "flower-qr-card", "gatsby-qr-card"] as const;
const DEFAULT_STACK_CARD_IDS = ["eucalyptus-qr-card", "default-qr-card", "balloons-qr-card"] as const;

// Hovering the stack fans the cards out like a hand of cards.
const stackCardClasses = ["left-1/2 top-1/2 -translate-x-[65%] -translate-y-[55%] -rotate-6 can-hover:group-hover/stack:-translate-x-[98%] can-hover:group-hover/stack:-translate-y-[50%] can-hover:group-hover/stack:-rotate-[13deg]", "left-1/2 top-1/2 -translate-x-[50%] -translate-y-[50%] rotate-0 z-10 can-hover:group-hover/stack:-translate-y-[57%]", "left-1/2 top-1/2 -translate-x-[35%] -translate-y-[45%] rotate-6 can-hover:group-hover/stack:-translate-x-[2%] can-hover:group-hover/stack:-translate-y-[42%] can-hover:group-hover/stack:rotate-[12deg]"] as const;
function getExpandedCardOrigin(index: number) {
  return {
    x: 220 - index * 52,
    y: -170 + Math.min(index, 4) * 8,
    rotate: -9 + index * 2.5
  };
}
function resolveCardsByIds<T extends {
  id: string;
}>(cards: T[], ids: readonly string[]) {
  return ids.map(id => cards.find(card => card.id === id)).filter((card): card is T => Boolean(card));
}
export default function PocketCardsShowcase(t0) {
  const $ = _c(149);
  const {
    locale,
    autoExpand: t1,
    squareCardPreviews: t2,
    copy,
    expandedCardIds,
    stackCardIds,
    theme
  } = t0;
  const autoExpand = t1 === undefined ? true : t1;
  const squareCardPreviews = t2 === undefined ? false : t2;
  const [isExpanded, setIsExpanded] = React.useState(false);
  const sectionRef = React.useRef(null);
  const hasAutoExpandedRef = React.useRef(false);
  const userToggledRef = React.useRef(false);
  const t = useTranslations("Index.QRTemplates");
  const tPrint = useTranslations("Home.Event.Templates.printYourself");
  const previewLocale = supportedLocales.has(locale) ? locale as QrTemplatePreviewLocale : "en";
  let t3;
  if ($[0] !== previewLocale) {
    t3 = STORED_QR_TEMPLATE_PRESETS.filter(preset => preset.locales.includes(previewLocale)).map(preset_0 => ({
      id: preset_0.id,
      label: preset_0.name,
      src: getStoredQrTemplatePreviewImageUrl(preset_0, previewLocale),
      variants: preset_0.templateJson.config.variants.map(_temp),
      hasTasks: true
    }));
    $[0] = previewLocale;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const allCards = t3;
  let t4;
  if ($[2] !== expandedCardIds) {
    t4 = expandedCardIds && expandedCardIds.length > 0 ? expandedCardIds.slice(0, 6) : Array.from(DEFAULT_EXPANDED_CARD_IDS);
    $[2] = expandedCardIds;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const resolvedExpandedCardIds = t4;
  let t5;
  if ($[4] !== allCards || $[5] !== resolvedExpandedCardIds) {
    t5 = resolveCardsByIds(allCards, resolvedExpandedCardIds.slice(0, 5));
    $[4] = allCards;
    $[5] = resolvedExpandedCardIds;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const expandedCards = t5;
  let t6;
  if ($[7] !== allCards || $[8] !== resolvedExpandedCardIds) {
    t6 = resolveCardsByIds(allCards, resolvedExpandedCardIds);
    $[7] = allCards;
    $[8] = resolvedExpandedCardIds;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const mobileExpandedCards = t6;
  let t7;
  if ($[10] !== allCards || $[11] !== stackCardIds) {
    const ids = stackCardIds && stackCardIds.length > 0 ? stackCardIds.slice(0, 3) : Array.from(DEFAULT_STACK_CARD_IDS);
    t7 = resolveCardsByIds(allCards, ids);
    $[10] = allCards;
    $[11] = stackCardIds;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const resolved = t7;
  let t8;
  if ($[13] !== expandedCards || $[14] !== resolved) {
    t8 = resolved.length > 0 ? resolved : expandedCards.slice(0, 3);
    $[13] = expandedCards;
    $[14] = resolved;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const stackCards = t8;
  let t9;
  if ($[16] !== t) {
    t9 = t("badge");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t) {
    t10 = t("title");
    $[18] = t;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t) {
    t11 = t("body");
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t) {
    t12 = t("subtitle");
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t) {
    t13 = t("moreNote");
    $[24] = t;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== copy || $[27] !== t10 || $[28] !== t11 || $[29] !== t12 || $[30] !== t13 || $[31] !== t9) {
    t14 = {
      badge: t9,
      title: t10,
      body: t11,
      subtitle: t12,
      bottomNote: t13,
      ...copy
    };
    $[26] = copy;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t9;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const content = t14;
  let t15;
  let t16;
  if ($[33] !== autoExpand) {
    t15 = () => {
      const sectionElement = sectionRef.current;
      if (!autoExpand || !sectionElement || hasAutoExpandedRef.current || userToggledRef.current) {
        return;
      }
      const tryAutoExpand = (triggerRatio, entry) => {
        if (hasAutoExpandedRef.current || userToggledRef.current) {
          return false;
        }
        const rect = entry?.boundingClientRect ?? sectionElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const triggerLine = viewportHeight * triggerRatio;
        const sectionHasEnteredViewport = rect.top <= triggerLine;
        const sectionStillVisible = rect.bottom >= viewportHeight * 0.2;
        if (!sectionHasEnteredViewport || !sectionStillVisible) {
          return false;
        }
        hasAutoExpandedRef.current = true;
        setIsExpanded(true);
        return true;
      };
      const isMobileViewport = _temp2;
      if (isMobileViewport()) {
        const handleViewportChange = () => {
          if (tryAutoExpand(0.11)) {
            window.removeEventListener("scroll", handleViewportChange);
            window.removeEventListener("resize", handleViewportChange);
          }
        };
        window.addEventListener("scroll", handleViewportChange, {
          passive: true
        });
        window.addEventListener("resize", handleViewportChange);
        handleViewportChange();
        return () => {
          window.removeEventListener("scroll", handleViewportChange);
          window.removeEventListener("resize", handleViewportChange);
        };
      }
      const observer = new IntersectionObserver(t17 => {
        const [entry_0] = t17;
        if (!entry_0?.isIntersecting) {
          return;
        }
        if (tryAutoExpand(0.68, entry_0)) {
          observer.disconnect();
        }
      }, {
        root: null,
        rootMargin: "0px 0px -36% 0px",
        threshold: 0.68
      });
      observer.observe(sectionElement);
      return () => observer.disconnect();
    };
    t16 = [autoExpand];
    $[33] = autoExpand;
    $[34] = t15;
    $[35] = t16;
  } else {
    t15 = $[34];
    t16 = $[35];
  }
  React.useEffect(t15, t16);
  let t17;
  if ($[36] !== isExpanded) {
    t17 = () => {
      userToggledRef.current = true;
      setIsExpanded(!isExpanded);
    };
    $[36] = isExpanded;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const handleExpandToggle = t17;
  let t18;
  if ($[38] !== isExpanded || $[39] !== squareCardPreviews || $[40] !== tPrint) {
    t18 = (card, index, keySuffix) => {
      const origin = getExpandedCardOrigin(index);
      return <motion.div key={`${card.id}${keySuffix ?? ""}`} initial={false} animate={isExpanded ? {
        opacity: 1,
        x: 0,
        y: 0,
        rotate: 0,
        scale: 1
      } : {
        opacity: 0,
        x: origin.x,
        y: origin.y,
        rotate: origin.rotate,
        scale: 0.72
      }} transition={{
        type: "spring",
        stiffness: 240,
        damping: 24,
        delay: isExpanded ? index * 0.03 : 0
      }} className={cn("flex h-full w-full max-w-[152px] flex-col sm:max-w-[168px] md:max-w-[176px]", squareCardPreviews ? "bg-transparent" : "rounded-2xl bg-white p-2 shadow-md")}><div className={cn("overflow-hidden", squareCardPreviews ? "rounded-none" : "rounded-xl")}><Image alt={`${card.label} print-ready card`} className={cn("h-auto w-full", squareCardPreviews ? "rounded-none" : "rounded-xl")} height={960} loading="lazy" sizes="(min-width: 1024px) 176px, (min-width: 640px) 168px, 152px" src={card.src} width={640} /></div><div className="mt-auto space-y-2 px-1 pb-1 pt-3"><div className="flex flex-wrap items-center gap-1.5"><span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#7a6b5f]">{tPrint("colorVariants")}</span><div className="flex flex-wrap gap-1.5">{card.variants.slice(0, 4).map(_temp3)}</div></div>{card.hasTasks ? <div className="flex items-end gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#7a6b5f]"><span>{tPrint("photoChallenges")}</span><Check className="mb-px h-3.5 w-3.5 shrink-0 self-end text-[#5d8a62]" /></div> : null}</div></motion.div>;
    };
    $[38] = isExpanded;
    $[39] = squareCardPreviews;
    $[40] = tPrint;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const renderExpandedCard = t18;
  const t19 = theme?.sectionStyle;
  const t20 = theme?.badgeStyle;
  let t21;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Palette className="h-3.5 w-3.5" />;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== content.badge || $[44] !== t20) {
    t22 = <div className="inline-flex items-center gap-2 self-start rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary" style={t20}>{t21}{content.badge}</div>;
    $[43] = content.badge;
    $[44] = t20;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  const t23 = theme?.titleStyle;
  let t24;
  if ($[46] !== content.title || $[47] !== t23) {
    t24 = <h2 className="text-3xl font-bold md:text-4xl" style={t23}>{content.title}</h2>;
    $[46] = content.title;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  const t25 = theme?.bodyStyle;
  let t26;
  if ($[49] !== content.body || $[50] !== t25) {
    t26 = <p className="text-lg leading-relaxed text-muted-foreground" style={t25}>{content.body}</p>;
    $[49] = content.body;
    $[50] = t25;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  const t27 = theme?.subtitleStyle;
  let t28;
  if ($[52] !== content.subtitle || $[53] !== t27) {
    t28 = <p className="text-sm text-muted-foreground" style={t27}>{content.subtitle}</p>;
    $[52] = content.subtitle;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  const t29 = theme?.loginButtonVariant ?? "dark";
  const t30 = theme?.loginButtonClassName;
  let t31;
  if ($[55] !== t30) {
    t31 = cn("w-full sm:w-fit", t30);
    $[55] = t30;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  const t32 = theme?.loginButtonStyle;
  let t33;
  if ($[57] !== t) {
    t33 = t("loginButton");
    $[57] = t;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  let t34;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <ArrowRight className="h-4 w-4" />;
    $[59] = t34;
  } else {
    t34 = $[59];
  }
  let t35;
  if ($[60] !== t33) {
    t35 = <Link href="/login"><span className="flex items-center gap-2">{t33}{t34}</span></Link>;
    $[60] = t33;
    $[61] = t35;
  } else {
    t35 = $[61];
  }
  let t36;
  if ($[62] !== t29 || $[63] !== t31 || $[64] !== t32 || $[65] !== t35) {
    t36 = <Button asChild={true} variant={t29} className={t31} style={t32}>{t35}</Button>;
    $[62] = t29;
    $[63] = t31;
    $[64] = t32;
    $[65] = t35;
    $[66] = t36;
  } else {
    t36 = $[66];
  }
  const t37 = theme?.expandButtonClassName;
  let t38;
  if ($[67] !== t37) {
    t38 = cn("w-full sm:w-fit", t37);
    $[67] = t37;
    $[68] = t38;
  } else {
    t38 = $[68];
  }
  const t39 = theme?.expandButtonStyle;
  let t40;
  if ($[69] !== isExpanded || $[70] !== t) {
    t40 = isExpanded ? t("collapseButton") : t("expandButton");
    $[69] = isExpanded;
    $[70] = t;
    $[71] = t40;
  } else {
    t40 = $[71];
  }
  const t41 = isExpanded ? 180 : 0;
  let t42;
  if ($[72] !== t41) {
    t42 = {
      rotate: t41
    };
    $[72] = t41;
    $[73] = t42;
  } else {
    t42 = $[73];
  }
  let t43;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = {
      duration: 0.24,
      ease: "easeOut"
    };
    $[74] = t43;
  } else {
    t43 = $[74];
  }
  let t44;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <ArrowDown className="h-4 w-4" />;
    $[75] = t44;
  } else {
    t44 = $[75];
  }
  let t45;
  if ($[76] !== t42) {
    t45 = <motion.span animate={t42} transition={t43} className="inline-flex">{t44}</motion.span>;
    $[76] = t42;
    $[77] = t45;
  } else {
    t45 = $[77];
  }
  let t46;
  if ($[78] !== t40 || $[79] !== t45) {
    t46 = <span className="flex items-center gap-2">{t40}{t45}</span>;
    $[78] = t40;
    $[79] = t45;
    $[80] = t46;
  } else {
    t46 = $[80];
  }
  let t47;
  if ($[81] !== handleExpandToggle || $[82] !== isExpanded || $[83] !== t38 || $[84] !== t39 || $[85] !== t46) {
    t47 = <Button variant="outline" className={t38} style={t39} onClick={handleExpandToggle} aria-expanded={isExpanded} aria-controls="pocket-card-expanded">{t46}</Button>;
    $[81] = handleExpandToggle;
    $[82] = isExpanded;
    $[83] = t38;
    $[84] = t39;
    $[85] = t46;
    $[86] = t47;
  } else {
    t47 = $[86];
  }
  let t48;
  if ($[87] !== t36 || $[88] !== t47) {
    t48 = <div className="flex flex-col gap-3 sm:flex-row">{t36}{t47}</div>;
    $[87] = t36;
    $[88] = t47;
    $[89] = t48;
  } else {
    t48 = $[89];
  }
  let t49;
  if ($[90] !== t22 || $[91] !== t24 || $[92] !== t26 || $[93] !== t28 || $[94] !== t48) {
    t49 = <div className="flex flex-col gap-6">{t22}{t24}{t26}{t28}{t48}</div>;
    $[90] = t22;
    $[91] = t24;
    $[92] = t26;
    $[93] = t28;
    $[94] = t48;
    $[95] = t49;
  } else {
    t49 = $[95];
  }
  const t50 = isExpanded ? "max-h-[2200px] opacity-100" : "max-h-0 opacity-0";
  let t51;
  if ($[96] !== t50) {
    t51 = cn("overflow-hidden transition-[max-height,opacity,padding] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] md:hidden", t50);
    $[96] = t50;
    $[97] = t51;
  } else {
    t51 = $[97];
  }
  let t52;
  if ($[98] !== mobileExpandedCards || $[99] !== renderExpandedCard) {
    let t53;
    if ($[101] !== renderExpandedCard) {
      t53 = (card_0, index_0) => renderExpandedCard(card_0, index_0, "-mobile");
      $[101] = renderExpandedCard;
      $[102] = t53;
    } else {
      t53 = $[102];
    }
    t52 = mobileExpandedCards.map(t53);
    $[98] = mobileExpandedCards;
    $[99] = renderExpandedCard;
    $[100] = t52;
  } else {
    t52 = $[100];
  }
  let t53;
  if ($[103] !== t52) {
    t53 = <div className="grid grid-cols-2 justify-items-center gap-3">{t52}</div>;
    $[103] = t52;
    $[104] = t53;
  } else {
    t53 = $[104];
  }
  let t54;
  if ($[105] !== content.bottomNote || $[106] !== theme?.subtitleStyle) {
    t54 = content.bottomNote ? <p className="mx-auto mt-5 max-w-md text-center text-xs leading-relaxed text-muted-foreground" style={theme?.subtitleStyle}>{content.bottomNote}</p> : null;
    $[105] = content.bottomNote;
    $[106] = theme?.subtitleStyle;
    $[107] = t54;
  } else {
    t54 = $[107];
  }
  let t55;
  if ($[108] !== t53 || $[109] !== t54) {
    t55 = <div id="pocket-card-expanded-mobile" className="pt-2">{t53}{t54}</div>;
    $[108] = t53;
    $[109] = t54;
    $[110] = t55;
  } else {
    t55 = $[110];
  }
  let t56;
  if ($[111] !== t51 || $[112] !== t55) {
    t56 = <div className={t51}>{t55}</div>;
    $[111] = t51;
    $[112] = t55;
    $[113] = t56;
  } else {
    t56 = $[113];
  }
  let t57;
  if ($[114] !== squareCardPreviews || $[115] !== stackCards) {
    let t58;
    if ($[117] !== squareCardPreviews) {
      t58 = (card_1, index_1) => <div key={card_1.id} className={cn("absolute w-40 overflow-hidden shadow-lg transition-transform duration-500 ease-[var(--ease-overshoot)] md:w-48", squareCardPreviews ? "rounded-none" : "rounded-xl", stackCardClasses[index_1], index_1 === 1 ? "shadow-xl" : "")}><Image alt={`${card_1.label} pocket card`} className="h-auto w-full" height={960} loading="lazy" sizes="(min-width: 768px) 192px, 160px" src={card_1.src} width={640} /></div>;
      $[117] = squareCardPreviews;
      $[118] = t58;
    } else {
      t58 = $[118];
    }
    t57 = stackCards.map(t58);
    $[114] = squareCardPreviews;
    $[115] = stackCards;
    $[116] = t57;
  } else {
    t57 = $[116];
  }
  let t58;
  if ($[119] !== t57) {
    t58 = <div className="group/stack relative hidden min-h-[400px] items-center justify-center md:flex">{t57}</div>;
    $[119] = t57;
    $[120] = t58;
  } else {
    t58 = $[120];
  }
  let t59;
  if ($[121] !== t49 || $[122] !== t56 || $[123] !== t58) {
    t59 = <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">{t49}{t56}{t58}</div>;
    $[121] = t49;
    $[122] = t56;
    $[123] = t58;
    $[124] = t59;
  } else {
    t59 = $[124];
  }
  const t60 = isExpanded ? "max-h-[1100px] opacity-100" : "max-h-0 opacity-0";
  let t61;
  if ($[125] !== t60) {
    t61 = cn("hidden overflow-hidden transition-[max-height,opacity,padding] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] md:block", t60);
    $[125] = t60;
    $[126] = t61;
  } else {
    t61 = $[126];
  }
  let t62;
  if ($[127] !== expandedCards || $[128] !== renderExpandedCard) {
    let t63;
    if ($[130] !== renderExpandedCard) {
      t63 = (card_2, index_2) => renderExpandedCard(card_2, index_2);
      $[130] = renderExpandedCard;
      $[131] = t63;
    } else {
      t63 = $[131];
    }
    t62 = expandedCards.map(t63);
    $[127] = expandedCards;
    $[128] = renderExpandedCard;
    $[129] = t62;
  } else {
    t62 = $[129];
  }
  let t63;
  if ($[132] !== t62) {
    t63 = <div className="grid grid-cols-5 justify-items-center gap-3 md:gap-4">{t62}</div>;
    $[132] = t62;
    $[133] = t63;
  } else {
    t63 = $[133];
  }
  let t64;
  if ($[134] !== content.bottomNote || $[135] !== theme?.subtitleStyle) {
    t64 = content.bottomNote ? <p className="mx-auto mt-5 max-w-xl text-center text-sm leading-relaxed text-muted-foreground" style={theme?.subtitleStyle}>{content.bottomNote}</p> : null;
    $[134] = content.bottomNote;
    $[135] = theme?.subtitleStyle;
    $[136] = t64;
  } else {
    t64 = $[136];
  }
  let t65;
  if ($[137] !== t63 || $[138] !== t64) {
    t65 = <div id="pocket-card-expanded" className="pt-8">{t63}{t64}</div>;
    $[137] = t63;
    $[138] = t64;
    $[139] = t65;
  } else {
    t65 = $[139];
  }
  let t66;
  if ($[140] !== t61 || $[141] !== t65) {
    t66 = <div className={t61}>{t65}</div>;
    $[140] = t61;
    $[141] = t65;
    $[142] = t66;
  } else {
    t66 = $[142];
  }
  let t67;
  if ($[143] !== t59 || $[144] !== t66) {
    t67 = <div className="mx-auto max-w-screen-lg">{t59}{t66}</div>;
    $[143] = t59;
    $[144] = t66;
    $[145] = t67;
  } else {
    t67 = $[145];
  }
  let t68;
  if ($[146] !== t19 || $[147] !== t67) {
    t68 = <div ref={sectionRef} className="w-full bg-muted/50 px-4 py-20" style={t19}>{t67}</div>;
    $[146] = t19;
    $[147] = t67;
    $[148] = t68;
  } else {
    t68 = $[148];
  }
  return t68;
}
function _temp3(variant_0) {
  return <div key={variant_0.id} title={variant_0.name} className="rounded-full border border-black/6 bg-[#f7f1ea]/70 p-1"><span className="block h-3.5 w-3.5 rounded-full border border-black/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]" style={{
      background: variant_0.style === "gradient" ? `linear-gradient(135deg, ${variant_0.swatchStart}, ${variant_0.swatchEnd})` : variant_0.swatchStart
    }} /></div>;
}
function _temp2() {
  return window.innerWidth < 768;
}
function _temp(variant) {
  return {
    id: variant.id,
    name: variant.name,
    swatchStart: variant.swatchStart,
    swatchEnd: variant.swatchEnd,
    style: variant.style
  };
}